import authService from "../../utils/authService";
import {
  DEFAULT_TIMER_SECONDS,
  EXTEND_SECONDS,
  MAX_ATTENDANCE_ROUNDS,
  MAX_GROUPS,
  MAX_STUDENTS_PER_GROUP,
  MIN_GROUPS,
} from "../data/attendanceConstants";
import type {
  ApiGroup,
  ApiRoundStats,
  AttendanceConfig,
  AttendanceGroup,
  AttendanceImage,
  AttendanceReportRow,
  AttendanceRound,
  BatchStudentReport,
  BatchStudentRow,
  CreateSessionPayload,
  CreateSessionResponse,
  RemarkOption,
  RoundStats,
  StartRoundResponse,
  LatestRoundResponse,
  StudentAttendanceRecord,
  TodayEventOption,
  TodayEventsPayload,
} from "../types/attendance";

const BASE = "attendance";

function mapImage(raw: { image_id: string; label: string; src_url: string }): AttendanceImage {
  return { id: raw.image_id, label: raw.label, url: raw.src_url };
}

function mapGroup(raw: ApiGroup): AttendanceGroup {
  return {
    id: raw.group_id,
    name: raw.group_name,
    correctImageId: raw.correct_image_id,
    correctImageUrl: raw.image_src_url,
    studentCount: raw.student_count,
  };
}

function mapRoundStats(raw: ApiRoundStats): RoundStats {
  return {
    roundNo: raw.round_no,
    roundLabel: raw.round_label,
    presentPercent: raw.present_percent,
    absentPercent: raw.absent_percent,
    total: raw.total,
    timerSeconds: raw.timer_seconds,
    presentCount: raw.present_count,
    absentCount: raw.absent_count,
    manualHeadCount: raw.manual_head_count ?? null,
  };
}

export async function fetchAttendanceConfig(): Promise<AttendanceConfig> {
  const response = await authService.get(`${BASE}/config/`);
  if (response.status !== 200 || !response.data) {
    throw new Error("Attendance config unavailable");
  }
  const data = response.data;
  return {
    defaultTimerSeconds: data.default_timer_seconds ?? DEFAULT_TIMER_SECONDS,
    extendSeconds: data.extend_seconds ?? EXTEND_SECONDS,
    maxAttendanceRounds: data.max_attendance_rounds ?? MAX_ATTENDANCE_ROUNDS,
    maxStudentsPerGroup: data.max_students_per_group ?? MAX_STUDENTS_PER_GROUP,
    minGroups: data.min_groups ?? MIN_GROUPS,
    maxGroups: data.max_groups ?? MAX_GROUPS,
    activeImageCount: data.active_image_count ?? (data.images ?? []).length,
    effectiveMaxGroups: data.effective_max_groups,
    maxCapacityStudents: data.max_capacity_students,
    studentChoiceCount: data.student_choice_count,
    images: (data.images ?? []).map(mapImage),
    remarkOptions: (data.remark_options ?? []).map(
      (r: { remark_code: string; remark_label: string }): RemarkOption => ({
        remarkCode: r.remark_code,
        remarkLabel: r.remark_label,
      }),
    ),
  };
}

export function toSessionDatetime(dateLabel: string, time: string): string {
  const parsed = new Date(`${dateLabel} ${time}`);
  if (Number.isNaN(parsed.getTime())) {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${time}`;
  }
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${time}`;
}

function mapTodayEvent(raw: Record<string, unknown>): TodayEventOption {
  return {
    testId: String(raw.test_id ?? ""),
    eventName: String(raw.event_name ?? raw.test_name ?? ""),
    eventType: String(raw.event_type ?? raw.test_type ?? ""),
    batchId: String(raw.batch_id ?? ""),
    batchName: raw.batch_name ? String(raw.batch_name) : undefined,
    scheduledAt: raw.scheduled_at ? String(raw.scheduled_at) : undefined,
    displayLabel: String(
      raw.display_label ?? raw.event_name ?? raw.test_name ?? "",
    ),
  };
}

export async function fetchTodayEvents(
  payload: TodayEventsPayload,
): Promise<TodayEventOption[]> {
  const response = await authService.post(`${BASE}/events/today/`, payload);
  const events = response.data?.events ?? response.data?.tests;
  return Array.isArray(events)
    ? (events as Record<string, unknown>[]).map(mapTodayEvent)
    : [];
}

export async function createAttendanceSession(
  payload: CreateSessionPayload,
): Promise<CreateSessionResponse> {
  const response = await authService.post(`${BASE}/create_session/`, payload);
  if (response.status !== 200 || !response.data?.attendance_session_id) {
    const detail = response.data?.detail;
    const error = new Error(
      typeof detail === "string" && detail.trim()
        ? detail
        : "Failed to create attendance session",
    );
    (error as { response?: unknown }).response = response;
    throw error;
  }
  return response.data as CreateSessionResponse;
}

export async function startAttendanceRound(
  attendanceSessionId: string,
  timerSeconds?: number,
): Promise<StartRoundResponse> {
  const payload: {
    attendance_session_id: string;
    timer_seconds?: number;
  } = {
    attendance_session_id: attendanceSessionId,
  };
  if (timerSeconds != null && Number.isFinite(timerSeconds)) {
    payload.timer_seconds = timerSeconds;
  }
  const response = await authService.post(`${BASE}/start_round/`, payload);
  return response.data as StartRoundResponse;
}

export async function extendAttendanceRound(
  attendanceRoundId: string,
  extendSeconds: number,
): Promise<RoundStats> {
  const response = await authService.post(`${BASE}/extend_round/`, {
    attendance_round_id: attendanceRoundId,
    extend_seconds: extendSeconds,
  });
  return mapRoundStats(response.data as ApiRoundStats);
}

export async function updateAttendanceHeadcount(
  attendanceRoundId: string,
  manualHeadCount: number,
): Promise<RoundStats> {
  const response = await authService.post(`${BASE}/update_headcount/`, {
    attendance_round_id: attendanceRoundId,
    manual_head_count: manualHeadCount,
  });
  return mapRoundStats(response.data as ApiRoundStats);
}

export async function fetchPlannedGroups(
  attendanceSessionId: string,
): Promise<AttendanceGroup[]> {
  const response = await authService.get(
    `${BASE}/session/${attendanceSessionId}/planned_groups/`,
  );
  const groups = Array.isArray(response.data) ? response.data : [];
  return (groups as ApiGroup[]).map(mapGroup);
}

export async function fetchLatestSessionRound(
  attendanceSessionId: string,
): Promise<StartRoundResponse | null> {
  const response = await authService.get(
    `${BASE}/session/${attendanceSessionId}/latest_round/`,
  );
  const data = (response.data ?? {}) as LatestRoundResponse;
  if (!data.has_round || !data.attendance_round_id || !data.round_stats) {
    return null;
  }
  return {
    success: true,
    message: "Existing attendance round",
    attendance_round_id: data.attendance_round_id,
    round_no: data.round_no ?? data.round_stats.round_no,
    round_label: data.round_label ?? data.round_stats.round_label,
    groups: data.groups ?? [],
    round_stats: data.round_stats,
  };
}

export async function fetchRoundGroups(attendanceRoundId: string): Promise<AttendanceGroup[]> {
  const response = await authService.get(`${BASE}/round/${attendanceRoundId}/groups/`);
  const groups = Array.isArray(response.data) ? response.data : response.data?.groups ?? [];
  return (groups as ApiGroup[]).map(mapGroup);
}

export async function fetchRoundStats(attendanceRoundId: string): Promise<RoundStats> {
  const response = await authService.get(`${BASE}/round/${attendanceRoundId}/stats/`);
  return mapRoundStats(response.data as ApiRoundStats);
}

function mapReportRound(raw: {
  round_no: number;
  label: string;
  present_percent: number;
  absent_percent: number;
  total: number;
}): AttendanceRound {
  return {
    roundNo: raw.round_no,
    label: raw.label,
    presentPercent: raw.present_percent,
    absentPercent: raw.absent_percent,
    total: raw.total,
  };
}

function mapReportRow(raw: Record<string, unknown>): AttendanceReportRow {
  return {
    slNo: raw.sl_no as number,
    attendanceSessionId: raw.attendance_session_id as string,
    batchId: raw.batch_id as string | undefined,
    batchIds: Array.isArray(raw.batch_ids)
      ? (raw.batch_ids as string[])
      : undefined,
    date: raw.date as string,
    purpose: raw.purpose as string,
    course: raw.course as string,
    batch: raw.batch as string,
    branch: raw.branch as string,
    name: raw.name as string | undefined,
    attendanceRounds: ((raw.attendance_rounds as unknown[]) ?? []).map((r) =>
      mapReportRound(r as Parameters<typeof mapReportRound>[0]),
    ),
  };
}

export interface AttendanceReportFilters {
  course_name?: string;
  batch_names?: string[];
  branch_name?: string;
  setup_mode?: string;
}

export async function fetchAttendanceReport(
  filters: AttendanceReportFilters = {},
): Promise<AttendanceReportRow[]> {
  const payload: Record<string, unknown> = {};
  if (filters.course_name && filters.course_name !== "All") {
    payload.course_name = filters.course_name;
  }
  if (filters.batch_names && filters.batch_names.length > 0) {
    payload.batch_names = filters.batch_names;
  }
  if (filters.branch_name && filters.branch_name !== "All") {
    payload.branch_name = filters.branch_name;
  }
  if (filters.setup_mode && filters.setup_mode !== "All") {
    payload.setup_mode = filters.setup_mode;
  }

  const response = await authService.post(`${BASE}/report/`, payload);
  if (response.status !== 200 || !response.data?.rows) {
    throw new Error("Attendance report unavailable");
  }
  return (response.data.rows as Record<string, unknown>[]).map(mapReportRow);
}

function mapStudentRecord(raw: Record<string, unknown>): StudentAttendanceRecord {
  return {
    slNo: raw.sl_no as number,
    recordId: typeof raw.record_id === "number" ? raw.record_id : undefined,
    date: raw.date as string,
    purpose: raw.purpose as string,
    attendanceNo: raw.attendance_no as string,
    present: raw.present as "Y" | "N",
    remark: (raw.remark as string) ?? "",
  };
}

function mapBatchStudent(raw: Record<string, unknown>): BatchStudentRow {
  return {
    studentName: raw.student_name as string,
    studentId: raw.student_id as string,
    usn: String(raw.usn ?? "").trim(),
    records: ((raw.records as unknown[]) ?? []).map((r) =>
      mapStudentRecord(r as Record<string, unknown>),
    ),
  };
}

export async function fetchBatchStudentReport(
  attendanceSessionId: string,
  batchId?: string,
): Promise<BatchStudentReport> {
  const payload: Record<string, unknown> = {
    attendance_session_id: attendanceSessionId,
  };
  if (batchId) {
    payload.batch_id = batchId;
  }
  const response = await authService.post(`${BASE}/report/batch/`, payload);
  if (response.status !== 200 || !response.data) {
    throw new Error("Batch student report unavailable");
  }
  const data = response.data;
  return {
    attendanceSessionId: data.attendance_session_id ?? attendanceSessionId,
    course: data.course,
    batch: data.batch,
    college: data.college,
    stream: data.stream,
    overallAttendancePercent: data.overall_attendance_percent,
    batchAverage: data.batch_average,
    students: (data.students ?? []).map((s: Record<string, unknown>) => mapBatchStudent(s)),
  };
}

export async function updateAttendanceRemark(
  recordId: number,
  remarkLabel: string,
): Promise<{ success: boolean; message: string; isPresent?: boolean }> {
  const response = await authService.patch(`${BASE}/record/${recordId}/remark/`, {
    remark_label: remarkLabel,
  });
  return {
    success: Boolean(response.data?.success),
    message: String(response.data?.message ?? "Student marked present with remark."),
    isPresent: response.data?.is_present,
  };
}

export async function markStudentPresentWithRemark(payload: {
  attendanceSessionId: string;
  studentId: string;
  attendanceNo: string;
  remarkLabel: string;
}): Promise<{ success: boolean; message: string; isPresent?: boolean }> {
  const response = await authService.post(`${BASE}/record/remark/`, {
    attendance_session_id: payload.attendanceSessionId,
    student_id: payload.studentId,
    attendance_no: payload.attendanceNo,
    remark_label: payload.remarkLabel,
  });
  return {
    success: Boolean(response.data?.success),
    message: String(response.data?.message ?? "Student marked present with remark."),
    isPresent: response.data?.is_present,
  };
}

export async function markAttendanceAbsent(
  recordId: number,
): Promise<{ success: boolean; message: string; isPresent?: boolean }> {
  const response = await authService.patch(
    `${BASE}/record/${recordId}/mark_absent/`,
  );
  return {
    success: Boolean(response.data?.success),
    message: String(response.data?.message ?? "Student marked absent."),
    isPresent: response.data?.is_present,
  };
}

export { mapGroup, mapRoundStats };
